import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    faPencil,
    faPlus,
    faTrash,
    faTimes,
    faArrowRight,
    faCopy,
    faCalendar,
    faBell,
    faCheck,
    faLayerGroup,
    faRepeat,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./Task.css";
import { Button } from "../../button/Button";
import { generateId } from "../../../utils/Utils";
import ActionMenu from "../../action-menu/ActionMenu";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import ListSelectionDialog from "../../list-selection-dialog/ListSelectionDialog";
import { autoResizeTextarea, handleShiftEnter, formatTaskDateRange, formatDateTimeDisplay, isOverdue, keepElementVisible, keepFocusedControlVisibleOnTouch } from "../../../utils/Utils";
import { ConfirmationDialog } from "../../confirmation-dialog/ConfirmationDialog";
import { TaskProps, Task as TaskType } from "../../../types";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { DatePickerButton } from "../../date-picker-button/DatePickerButton";
import { sanitizeElementTree } from "../../../utils/treeUtils";
import { getLastOverdueRecurringDueDate } from '../../../utils/taskRecurrence';

export const Task: React.FC<TaskProps> = ({
    todo,
    toggleComplete,
    deleteTodo,
    editTodo,
    updateTodo,
    subtasks,
    lists,
    selectedListId,
    moveTaskToList,
    moveSubtaskToList,
    onMoveToSection,
    subtaskInputPortalId,
    onExpandSubtasks,
    onSkipToLatestOverdue,
    level = 0,
}) => {
    const { t } = useTranslation();
    const [showInput, setShowInput] = useState(false);
    const [subtaskText, setSubtaskText] = useState("");
    const [subtaskDueDate, setSubtaskDueDate] = useState<Dayjs | null>(null);
    const [subtaskReminderTime, setSubtaskReminderTime] = useState<Dayjs | null>(null);
    const [showListSelectionDialog, setShowListSelectionDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [subtaskInputPortalTarget, setSubtaskInputPortalTarget] = useState<HTMLElement | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const subtaskEditorRef = useRef<HTMLDivElement>(null);
    const subtaskOptionsWereVisibleRef = useRef(false);

    useLayoutEffect(() => {
        const nextTarget = subtaskInputPortalId
            ? document.getElementById(subtaskInputPortalId)
            : null;

        setSubtaskInputPortalTarget((currentTarget) =>
            currentTarget === nextTarget ? currentTarget : nextTarget,
        );
    }, [subtaskInputPortalId, todo]);

    useEffect(() => {
        if (showInput && inputRef.current) {
            inputRef.current.focus();
            autoResizeTextarea({ target: inputRef.current });
            keepFocusedControlVisibleOnTouch(inputRef.current);
        }
    }, [showInput, subtaskInputPortalTarget]);

    useEffect(() => {
        if (inputRef.current) {
            autoResizeTextarea({ target: inputRef.current });
        }
    }, [subtaskText]);

    useEffect(() => {
        const optionsAreVisible = showInput && Boolean(subtaskText);
        if (!optionsAreVisible) {
            subtaskOptionsWereVisibleRef.current = false;
            return;
        }

        if (!subtaskOptionsWereVisibleRef.current && subtaskEditorRef.current) {
            subtaskOptionsWereVisibleRef.current = true;
            keepElementVisible(subtaskEditorRef.current);
        }
    }, [showInput, subtaskText]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (handleShiftEnter(e, subtaskText, setSubtaskText)) {
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmitSubtask(e);
        }
    };

    const handleShowSubtaskInput = () => {
        setShowInput(true);
        onExpandSubtasks?.();
    };

    const handleCopyTask = () => {
        navigator.clipboard.writeText(todo.title || todo.task || '');
    };

    const formatTaskWithSubtasks = (task: TaskType, level: number = 0): string => {
        const indent = '  '.repeat(level);
        const taskText = task.title || task.task || '';
        
        // Handle multi-line task text by indenting each line
        const lines = taskText.split('\n');
        let result = lines.map((line: string) => `${indent}${line}`).join('\n') + '\n';

        if (task.subtasks && task.subtasks.length > 0) {
            task.subtasks.forEach((subtask: TaskType) => {
                result += formatTaskWithSubtasks(subtask, level + 1);
            });
        }

        return result;
    };

    const handleCopyTaskWithSubtasks = () => {
        const taskWithSubtasks = { ...todo, subtasks: subtasks };
        const text = formatTaskWithSubtasks(taskWithSubtasks);
        navigator.clipboard.writeText(text.trim());
    };

    const actions = [
        {
            label: t("taskElement.editTask"),
            icon: faPencil,
            callback: () => editTodo(todo.id),
        },
        {
            label: t("taskElement.copy"),
            icon: faCopy,
            callback: handleCopyTask,
        },
        {
            label: t("taskElement.copyWithSubtasks"),
            icon: faCopy,
            callback: handleCopyTaskWithSubtasks,
        },
        {
            label: t("taskElement.moveToCollection"),
            icon: faArrowRight,
            callback: () => setShowListSelectionDialog(true),
        },
        ...(onMoveToSection ? [{
            label: t('sections.moveToSection'),
            icon: faLayerGroup,
            callback: () => onMoveToSection(todo),
        }] : []),
        ...(onSkipToLatestOverdue && todo.recurrence?.mode === 'fixed' && todo.dueDate
            && isOverdue(todo.dueDate, todo.duration, todo.schedule)
            && getLastOverdueRecurringDueDate(todo) !== todo.dueDate ? [{
                label: t('taskElement.skipToLatestOverdue'),
                icon: faCalendar,
                callback: () => onSkipToLatestOverdue(todo),
            }] : []),
        {
            label: t("taskElement.deleteTask"),
            icon: faTrash,
            callback: () => setShowDeleteConfirmationDialog(true),
        },
    ];

    const handleAddSubtask = () => {
        const newSubtask: TaskType = {
            id: generateId(),
            type: 'task',
            title: subtaskText,
            completed: false,
            edit: false,
            subtasks: [],
            dueDate: subtaskDueDate ? subtaskDueDate.toISOString() : undefined,
            schedule: subtaskDueDate ? { precision: 'day', startDate: subtaskDueDate.toISOString() } : undefined,
            reminderTime: subtaskReminderTime ? subtaskReminderTime.toISOString() : undefined,
        };

        const cleanedTodo = sanitizeElementTree([{
            ...todo,
            subtasks,
        }], level === 0)[0] as TaskType;
        const normalizedTodo: TaskType = {
            ...cleanedTodo,
            type: todo.type || 'task',
            title: todo.title || todo.task || '',
            collapsed: false,
        };

        const updatedSubtasks = [...(normalizedTodo.subtasks || []), newSubtask];
        const updatedTodo = { ...normalizedTodo, subtasks: updatedSubtasks };
        updateTodo(updatedTodo);
        resetSubtaskForm();
    };

    const handleSubmitSubtask = (e: React.FormEvent) => {
        e.preventDefault();
        if (!subtaskText) return;
        handleAddSubtask();
        if (inputRef.current) keepFocusedControlVisibleOnTouch(inputRef.current);
        // Keep the input field open after submission
    };

    const handleCancelSubtask = () => {
        resetSubtaskForm();
        setShowInput(false);
    };

    const handleSubtaskBlur = () => {
        if (!subtaskText.trim()) {
            handleCancelSubtask();
        }
    };

    const handleDeleteTask = () => {
        deleteTodo(todo.id);
        setShowDeleteConfirmationDialog(false);
    };

    const resetSubtaskForm = () => {
        setSubtaskText("");
        setSubtaskDueDate(null);
        setSubtaskReminderTime(null);
    };

    const subtaskEditor = (
        <div ref={subtaskEditorRef} className="new-subtask-container">
            <form
                className={"new-subtask"}
                onSubmit={handleSubmitSubtask}
            >
                <textarea
                    ref={inputRef}
                    value={subtaskText}
                    onChange={(e) => setSubtaskText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSubtaskBlur}
                    placeholder={t("taskElement.addSubtask")}
                    rows={1}
                    className={`formatted-input ${!subtaskText ? 'empty-subtask-input' : ''}`}
                />
                {!subtaskText && <button
                    type="button"
                    className="new-subtask-close"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleCancelSubtask}
                    aria-label={t("taskElement.cancelAddSubtask")}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>}
            </form>
            {subtaskText && (
                <div className="task-options-row subtask-options">
                    <div className="option-group-left">
                        <DatePickerButton
                            type="date"
                            value={subtaskDueDate}
                            onChange={setSubtaskDueDate}
                        />
                        <DatePickerButton
                            type="datetime"
                            value={subtaskReminderTime}
                            onChange={setSubtaskReminderTime}
                        />
                    </div>
                    <div className="option-group-right">
                        <Button
                            onClick={handleCancelSubtask}
                            type={"button"}
                            status={"secondary"}
                            text={""}
                            icon={faTimes}
                            ariaLabel={t("taskElement.cancelAddSubtask")}
                        />
                        <Button
                            onClick={handleSubmitSubtask}
                            type={"button"}
                            status={"primary"}
                            text={""}
                            icon={faPlus}
                            ariaLabel={t("taskElement.confirmAddSubtask")}
                        />
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="todo-wrapper">
            <div className={"todo-container"}>
                <button
                    type="button"
                    className={`element-kind-icon task-kind-icon ${todo.completed ? 'is-completed' : ''}`}
                    onClick={() => toggleComplete(todo.id)}
                    aria-label={todo.completed ? t('task.markIncomplete') : t('task.markComplete')}
                >
                    {todo.completed && <FontAwesomeIcon icon={faCheck} />}
                </button>
                <div className="todo-left-section">
                    <span className="element-type-label task-type-label">{t('elementTypes.task')}</span>
                    <div
                        className={`${todo.completed && "completed"} todo-text`}
                        onClick={() => toggleComplete(todo.id)}
                    >
                        {todo.title || todo.task}
                    </div>
                    {(todo.dueDate || todo.reminderTime || todo.recurrence) && (
                        <div
                            className="task-metadata"
                            onClick={() => toggleComplete(todo.id)}
                        >
                            {todo.dueDate && (
                                <div className="metadata-item">
                                    <FontAwesomeIcon
                                        icon={faCalendar}
                                        className="metadata-icon"
                                    />
                                    <span
                                        className={`metadata-text ${isOverdue(todo.dueDate, todo.duration, todo.schedule) ? "overdue" : ""}`}
                                    >
                                        {formatTaskDateRange(todo.dueDate, todo.schedule)}
                                    </span>
                                </div>
                            )}
                            {todo.reminderTime && (
                                <div className="metadata-item">
                                    <FontAwesomeIcon
                                        icon={faBell}
                                        className="metadata-icon"
                                    />
                                    <span className="metadata-text">
                                        {formatDateTimeDisplay(
                                            todo.reminderTime,
                                        )}
                                    </span>
                                </div>
                            )}
                            {todo.recurrence && (
                                <div className="metadata-item" title={todo.recurrence.mode === 'fixed' ? t('addTask.fixedSchedule') : t('addTask.afterCompletion')}>
                                    <FontAwesomeIcon icon={faRepeat} className="metadata-icon" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="todo-right-section">
                    <button
                        onClick={() => handleShowSubtaskInput()}
                        className="task-row-icon-button"
                        aria-label={t("taskElement.addSubtask")}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                    <ActionMenu
                        actions={actions}
                        icon={faEllipsisV}
                        buttonStyle={{}}
                        buttonClassName="task-row-icon-button"
                    />
                </div>
            </div>

            {showListSelectionDialog && (
                <ListSelectionDialog
                    lists={lists}
                    selectedListId={selectedListId}
                    onSelectList={(listId: string) => {
                        if (level > 0) {
                            moveSubtaskToList(todo, listId);
                        } else {
                            moveTaskToList(todo, listId);
                        }
                        setShowListSelectionDialog(false);
                    }}
                    onClose={() => setShowListSelectionDialog(false)}
                />
            )}

            {showInput && (subtaskInputPortalTarget ? createPortal(
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {subtaskEditor}
                </LocalizationProvider>,
                subtaskInputPortalTarget,
            ) : (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {subtaskEditor}
                </LocalizationProvider>
            ))}

            {showDeleteConfirmationDialog && (
                <ConfirmationDialog
                    title={t("taskElement.deleteTask")}
                    message={t("taskElement.deleteTaskConfirm")}
                    onConfirm={handleDeleteTask}
                    onCancel={() => setShowDeleteConfirmationDialog(false)}
                    isDanger={true}
                />
            )}
        </div>
    );
};
